import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../../common/types';
import { ConfirmDialog } from '../../../common/views/modals/confirmDialog/ConfirmDialog';
import { ConfirmDialogTileMark } from '../../../common/views/modals/confirmDialog/ConfirmDialogMark';
import { useConfirmWhileBusy } from '../../../common/views/modals/confirmDialog/useConfirmWhileBusy';
import { SMLButton } from '../../components/primitives/SMLButton';
import { SocialPlatform } from '../types';
import { platformMeta, platformName, platformSolidFill } from '../platformMeta';
import { youtubeConnectionMessages } from '../../youtubeConnection/youtubeConnectionMessages';

interface ChannelDisconnectButtonProps {
  platform: SocialPlatform;
  isSaving: boolean;
  onConfirm: (platform: SocialPlatform) => void;
}

/**
 * Dropping the tokens stops every scheduled post, so it asks first (design dialogs a1). The
 * dialog stays open with a spinner until the disconnect ends.
 */
const ChannelDisconnectButtonComponent: React.FC<ChannelDisconnectButtonProps> = ({
  platform,
  isSaving,
  onConfirm,
}) => {
  const intl = useIntl();
  const disconnect = useCallback(() => onConfirm(platform), [onConfirm, platform]);
  const dialog = useConfirmWhileBusy({ isBusy: isSaving, onConfirm: disconnect });

  const name = platformName(platform);
  const meta = platformMeta[platform];

  return (
    <>
      <SMLButton disabled={isSaving} onClick={dialog.open}>
        {intl.formatMessage({ id: 'app.sml.promote.channel.disconnect', defaultMessage: 'Disconnect' })}
      </SMLButton>
      <ConfirmDialog
        isOpen={dialog.isOpen}
        tone="danger"
        mark={
          <ConfirmDialogTileMark background={platformSolidFill(platform)} withRemoveBadge>
            <FontAwesomeIcon icon={meta.icon} />
          </ConfirmDialogTileMark>
        }
        title={intl.formatMessage(
          { id: 'app.sml.promote.channel.disconnect.confirmTitle', defaultMessage: 'Disconnect {platform}?' },
          { platform: name }
        )}
        description={
          // One YouTube sign-in for Social and Streaming: its Disconnect stops both.
          platform === 'youtube'
            ? intl.formatMessage(youtubeConnectionMessages.disconnectBody)
            : intl.formatMessage(
                {
                  id: 'app.sml.promote.channel.disconnect.confirmDescription',
                  defaultMessage:
                    'We stop posting to {platform} for you. Posts already booked wait for you to publish them. Your handle stays, so you can connect again later.',
                },
                { platform: name }
              )
        }
        confirmLabel={intl.formatMessage({
          id: 'app.sml.promote.channel.disconnect.confirmCta',
          defaultMessage: 'Disconnect',
        })}
        isBusy={dialog.isRunning}
        onConfirm={dialog.confirm}
        onCancel={dialog.cancel}
        testId="channel-disconnect-dialog"
      />
    </>
  );
};

export const ChannelDisconnectButton = TypedMemo(ChannelDisconnectButtonComponent);
